import { SEO_BRAND_LOGO, SEO_BRAND_LOGO_SIZE, SITE_URL } from '@/lib/seo';
import {
  getAuthorBySlug,
  resolveAuthorProfile,
  getAuthorProfilePath,
  isBrandAuthorSlug,
  type AuthorProfile,
} from '@/lib/authorCatalog';
import { BUSINESS_LEGAL } from '@/lib/businessLocation';
import { getLogicalPath, localizePath } from '@/lib/localeRouting';
import { getEntitySameAsUrls, getExtendedEntityProfileUrls } from '@/lib/entityProfiles';
import {
  BUSINESS_LOCATION,
  buildGeoCoordinatesSchema,
  buildPostalAddressSchema,
} from '@/lib/businessLocation';
import { CONTACT_PHONE_E164 } from '@/lib/contactDetails';
import { FIGTURES_AREA_SERVED, FIGTURES_FOUNDING_YEAR, getFigturesBrandSchemaDescription } from '@/lib/brandCopy';
import { COMPANY_DECLARED_COUNTS, getTechnologiesWeWorkWith } from '@/lib/companyDeclaredFacts.shared.js';

export type JsonLd = Record<string, unknown>;

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const PROFESSIONAL_SERVICE_ID = `${SITE_URL}/#professional-service`;

export const SPEAKABLE_CSS_SELECTOR = '.geo-direct-answer';

type SchemaLocale = 'tr' | 'en';

function absoluteUrlFromCanonicalPath(canonicalPath: string, locale?: SchemaLocale): string {
  const publicPath = locale ? localizePath(canonicalPath, locale) : canonicalPath;
  if (publicPath === '/') return SITE_URL;
  return `${SITE_URL}${publicPath.startsWith('/') ? publicPath : `/${publicPath}`}`;
}

function personId(slug: string) {
  return `${SITE_URL}/#person-${slug}`;
}

export function stripJsonLdContext(node: JsonLd): JsonLd {
  const { '@context': _context, ...rest } = node;
  return rest;
}

const LOCALIZED_URL_KEYS = new Set(['url', '@id', 'mainEntityOfPage', 'item']);

function localizeSiteUrl(value: string, key: string, locale: SchemaLocale): string {
  if (!value.startsWith(SITE_URL)) return value;
  const rest = value.slice(SITE_URL.length);
  if (rest !== '' && !rest.startsWith('/') && !rest.startsWith('#')) return value;
  const hashIndex = rest.indexOf('#');
  const pathname = (hashIndex === -1 ? rest : rest.slice(0, hashIndex)) || '/';
  const hash = hashIndex === -1 ? '' : rest.slice(hashIndex);
  // Root URLs are site-wide entity ids (Organization, WebSite, Person); only breadcrumb "Home" items follow the locale.
  if (pathname === '/' && key !== 'item') return value;
  if (pathname.includes('?') || /\.[a-z0-9]+$/i.test(pathname) || /^\/(\.well-known|api)(\/|$)/.test(pathname)) {
    return value;
  }
  return absoluteUrlFromCanonicalPath(getLogicalPath(pathname), locale) + hash;
}

/** Rewrites logical (EN) site URLs in url/@id/mainEntityOfPage/item to the locale's public URL so JSON-LD matches the canonical. */
export function localizeJsonLdUrls<T>(node: T, locale: SchemaLocale): T {
  if (Array.isArray(node)) return node.map((child) => localizeJsonLdUrls(child, locale)) as T;
  if (!node || typeof node !== 'object') return node;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    out[key] =
      typeof value === 'string' && LOCALIZED_URL_KEYS.has(key)
        ? localizeSiteUrl(value, key, locale)
        : localizeJsonLdUrls(value, locale);
  }
  return out as T;
}

export function buildSeoGraph(nodes: JsonLd[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@graph': nodes.map(stripJsonLdContext),
  };
}

export function buildOrganizationSchema(_description: string, lang: 'tr' | 'en' = 'en'): JsonLd {
  const sameAs = getEntitySameAsUrls();
  const { googleBusiness } = getExtendedEntityProfileUrls();
  const brandDescription = getFigturesBrandSchemaDescription(lang);
  const knowsAbout =
    lang === 'tr'
      ? [
          'Yazılım Geliştirme',
          'Yapay Zeka',
          'AI Ürün Mühendisliği',
          'Generative AI Entegrasyonu',
          'Web Uygulama Geliştirme',
          'SaaS Geliştirme',
          'MVP Geliştirme',
          'Mobil Uygulama Geliştirme',
          'Özel Yazılım',
          'ChatGPT Entegrasyonu',
          'Product Studio',
        ]
      : [
          'Software Development',
          'Artificial Intelligence',
          'AI Product Engineering',
          'Generative AI Integration',
          'Web Application Development',
          'SaaS Development',
          'MVP Development',
          'Product Design',
          'Full-Stack Engineering',
          'Mobile App Development',
          'Custom Software',
          'ChatGPT Integration',
          'Claude Automation',
          'Cursor Engineering Workflows',
          'Answer Engine Optimization',
          'Generative Engine Optimization',
          'Technical Consulting',
        ];
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'Figtures',
    legalName: BUSINESS_LEGAL.legalName,
    // "Figtures" is a coined word one character away from "figure"/"fixtures", so search
    // engines need the spelling variants they will actually encounter spelled out here.
    alternateName: ['Figtures Product Studio', 'Figtures AI Studio', 'Figtures.com', 'figtures'],
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      '@id': `${SITE_URL}/#logo`,
      url: SEO_BRAND_LOGO,
      contentUrl: SEO_BRAND_LOGO,
      width: SEO_BRAND_LOGO_SIZE,
      height: SEO_BRAND_LOGO_SIZE,
      caption: 'Figtures',
    },
    image: { '@id': `${SITE_URL}/#logo` },
    description: brandDescription,
    foundingDate: FIGTURES_FOUNDING_YEAR,
    areaServed: FIGTURES_AREA_SERVED,
    knowsLanguage: ['tr', 'en'],
    taxID: BUSINESS_LEGAL.vkn,
    numberOfEmployees: { '@type': 'QuantitativeValue', value: COMPANY_DECLARED_COUNTS.fullTimeEmployees },
    address: buildPostalAddressSchema(),
    geo: buildGeoCoordinatesSchema(),
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@figtures.com',
      telephone: CONTACT_PHONE_E164,
      contactType: 'customer service',
      availableLanguage: ['Turkish', 'English'],
    },
    ...(sameAs.length > 0 ? { sameAs } : {}),
    hasMap: googleBusiness || BUSINESS_LOCATION.googleMapsUrl,
    knowsAbout: [...new Set([...knowsAbout, ...getTechnologiesWeWorkWith(lang)])],
  };
}

export function buildPersonSchema(author: AuthorProfile, lang: 'tr' | 'en' = 'en'): JsonLd {
  const profilePath = getAuthorProfilePath(author.slug);
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': personId(author.slug),
    name: author.name,
    jobTitle: author.jobTitle[lang],
    description: author.bio[lang],
    url: `${SITE_URL}${localizePath(profilePath, lang)}`,
    ...(author.image ? { image: author.image } : {}),
    ...(author.sameAs.length > 0 ? { sameAs: author.sameAs } : {}),
    worksFor: { '@id': ORGANIZATION_ID },
  };
}

export function buildProfessionalServiceSchema(description: string, lang: 'tr' | 'en' = 'en'): JsonLd {
  const serviceNames =
    lang === 'tr'
      ? [
          'AI Geliştirme',
          'Özel Yazılım Geliştirme',
          'Mobil Uygulama Geliştirme',
          'Web Geliştirme',
          'SaaS / MVP Geliştirme',
          'UI/UX Tasarım',
          'AI Otomasyon / Agent',
          'Startup Ürün Mühendisliği',
        ]
      : [
          'AI Development',
          'Custom Software Development',
          'Mobile App Development',
          'Web Development',
          'SaaS / MVP Development',
          'UI/UX Design',
          'AI Automation / Agents',
          'Startup Product Engineering',
        ];

  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': PROFESSIONAL_SERVICE_ID,
    name: 'Figtures Product Studio',
    url: SITE_URL,
    email: 'contact@figtures.com',
    telephone: CONTACT_PHONE_E164,
    address: buildPostalAddressSchema(),
    geo: buildGeoCoordinatesSchema(),
    hasMap: BUSINESS_LOCATION.googleMapsUrl,
    provider: { '@id': ORGANIZATION_ID },
    areaServed: FIGTURES_AREA_SERVED,
    description,
    inLanguage: lang === 'tr' ? 'tr-TR' : 'en-US',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: lang === 'tr' ? 'Product Studio Hizmetleri' : 'Product Studio Services',
      itemListElement: serviceNames.map((name) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name },
      })),
    },
  };
}

/**
 * No `potentialAction`/SearchAction here on purpose: the site has no on-site search endpoint,
 * and a sitelinks searchbox pointing at a URL that does not handle a query is invalid markup.
 */
export function buildWebSiteSchema(lang: 'tr' | 'en'): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: 'Figtures',
    alternateName: ['Figtures.com', 'figtures'],
    url: SITE_URL,
    description:
      lang === 'tr'
        ? 'Figtures’ın resmi web sitesi — AI geliştirme, özel yazılım, SaaS/MVP ve mobil ürün teslimatı.'
        : 'Official website of Figtures — AI development, custom software, SaaS/MVP, and mobile product delivery.',
    inLanguage: [lang === 'tr' ? 'tr-TR' : 'en-US', lang === 'tr' ? 'en-US' : 'tr-TR'],
    publisher: { '@id': ORGANIZATION_ID },
  };
}

export function buildWebPageSchema({
  title,
  description,
  canonicalPath,
  inLanguage,
  schemaType = 'WebPage',
  locale,
}: {
  title: string;
  description: string;
  canonicalPath: string;
  inLanguage: 'tr-TR' | 'en-US';
  schemaType?: 'WebPage' | 'CollectionPage' | 'AboutPage' | 'ContactPage' | 'Article';
  /** When set, JSON-LD url/@id use the locale public path (must match link canonical). */
  locale?: SchemaLocale;
}): JsonLd {
  const absoluteUrl = absoluteUrlFromCanonicalPath(canonicalPath, locale);
  return {
    '@context': 'https://schema.org',
    '@type': schemaType,
    '@id': `${absoluteUrl}#webpage`,
    url: absoluteUrl,
    name: title,
    description,
    inLanguage,
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORGANIZATION_ID },
    publisher: { '@id': ORGANIZATION_ID },
  };
}

export function buildSpeakableSchema({
  canonicalPath,
  cssSelector = SPEAKABLE_CSS_SELECTOR,
  locale,
}: {
  canonicalPath: string;
  cssSelector?: string;
  locale?: SchemaLocale;
}): JsonLd {
  const absoluteUrl = absoluteUrlFromCanonicalPath(canonicalPath, locale);
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${absoluteUrl}#speakable`,
    url: absoluteUrl,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: [cssSelector],
    },
  };
}

export function buildCollectionItemListSchema({
  name,
  description,
  canonicalPath,
  items,
}: {
  name: string;
  description: string;
  canonicalPath: string;
  items: Array<{ name: string; url: string; position: number }>;
}): JsonLd {
  const absoluteUrl = canonicalPath === '/' ? SITE_URL : `${SITE_URL}${canonicalPath}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${absoluteUrl}#itemlist`,
    name,
    description,
    numberOfItems: items.length,
    itemListElement: items.map((item) => ({
      '@type': 'ListItem',
      position: item.position,
      name: item.name,
      url: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
    })),
  };
}

export function buildBreadcrumbSchema(items: Array<{ name: string; item: string }>): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((entry, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: entry.name,
      item: entry.item,
    })),
  };
}

export function buildSoftwareApplicationSchema({
  name,
  description,
  path,
  priceCurrency = 'TRY',
}: {
  name: string;
  description: string;
  path: string;
  priceCurrency?: string;
}): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    description,
    url: `${SITE_URL}${path}`,
    applicationCategory: 'WebApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency,
    },
    creator: {
      '@id': ORGANIZATION_ID,
    },
  };
}

export function buildServiceSchema({
  name,
  description,
  serviceType,
  slug,
}: {
  name: string;
  description: string;
  serviceType: string;
  slug?: string;
}): JsonLd {
  const serviceUrl = slug ? `${SITE_URL}/services/${slug}` : undefined;
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    ...(slug ? { '@id': serviceSchemaId(slug) } : {}),
    name,
    description,
    provider: {
      '@id': ORGANIZATION_ID,
    },
    serviceType,
    areaServed: 'Worldwide',
    ...(serviceUrl ? { url: serviceUrl } : {}),
  };
}

export function serviceSchemaId(slug: string): string {
  return `${SITE_URL}/services/${slug}#service`;
}

export function caseStudySchemaId(canonicalPath: string): string {
  const absoluteUrl = canonicalPath === '/' ? SITE_URL : `${SITE_URL}${canonicalPath}`;
  return `${absoluteUrl}#casestudy`;
}

export function buildCaseStudyGraph({
  name,
  description,
  canonicalPath,
  image,
  serviceSlugs,
  serviceNodes,
  breadcrumbItems,
  inLanguage,
  abstract,
}: {
  name: string;
  description: string;
  canonicalPath: string;
  image?: string;
  abstract?: string;
  serviceSlugs: string[];
  serviceNodes: JsonLd[];
  breadcrumbItems: Array<{ name: string; item: string }>;
  inLanguage: 'tr-TR' | 'en-US';
}): JsonLd {
  const absoluteUrl = canonicalPath === '/' ? SITE_URL : `${SITE_URL}${canonicalPath}`;
  const serviceRefs = serviceSlugs.map((slug) => ({ '@id': serviceSchemaId(slug) }));

  return buildSeoGraph([
    {
      '@type': 'CreativeWork',
      '@id': caseStudySchemaId(canonicalPath),
      name,
      description,
      url: absoluteUrl,
      inLanguage,
      ...(image ? { image } : {}),
      ...(abstract ? { abstract } : {}),
      creator: { '@id': ORGANIZATION_ID },
      publisher: { '@id': ORGANIZATION_ID },
      isPartOf: { '@id': WEBSITE_ID },
      about: serviceRefs,
      mentions: serviceRefs,
    },
    ...serviceNodes.map(stripJsonLdContext),
    stripJsonLdContext(buildBreadcrumbSchema(breadcrumbItems)),
  ]);
}

export function buildGuideArticleSchema({
  headline,
  description,
  path,
  inLanguage,
  aboutServiceSlug,
}: {
  headline: string;
  description: string;
  path: string;
  inLanguage: 'tr-TR' | 'en-US';
  aboutServiceSlug: string;
}): JsonLd {
  const absoluteUrl = `${SITE_URL}${path}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${absoluteUrl}#article`,
    headline,
    description,
    inLanguage,
    mainEntityOfPage: absoluteUrl,
    author: { '@id': ORGANIZATION_ID },
    publisher: { '@id': ORGANIZATION_ID },
    about: { '@id': serviceSchemaId(aboutServiceSlug) },
    isPartOf: { '@id': WEBSITE_ID },
  };
}

export function buildBlogPostingSchema({
  headline,
  description,
  path,
  datePublished,
  dateModified,
  image,
  inLanguage,
  authorName,
}: {
  headline: string;
  description: string;
  path: string;
  datePublished?: string | null;
  dateModified?: string | null;
  image?: string | null;
  inLanguage: 'tr-TR' | 'en-US';
  authorName?: string | null;
}): JsonLd {
  const author = resolveAuthorProfile(authorName);
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline,
    description,
    inLanguage,
    mainEntityOfPage: `${SITE_URL}${path}`,
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
    ...(image ? { image } : {}),
    author: authorSchemaReference(author),
    publisher: {
      '@id': ORGANIZATION_ID,
    },
    isPartOf: { '@id': WEBSITE_ID },
  };
}

export function buildArticleSchema({
  headline,
  description,
  path,
  dateModified,
  inLanguage,
  authorName,
}: {
  headline: string;
  description: string;
  path: string;
  dateModified?: string | null;
  inLanguage: 'tr-TR' | 'en-US';
  authorName?: string | null;
}): JsonLd {
  const author = resolveAuthorProfile(authorName);
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    inLanguage,
    mainEntityOfPage: `${SITE_URL}${path}`,
    ...(dateModified ? { dateModified } : {}),
    author: authorSchemaReference(author),
    publisher: {
      '@id': ORGANIZATION_ID,
    },
    isPartOf: { '@id': WEBSITE_ID },
  };
}

export function buildTechArticleSchema({
  headline,
  description,
  path,
  inLanguage,
  authorName,
}: {
  headline: string;
  description: string;
  path: string;
  inLanguage: 'tr-TR' | 'en-US';
  authorName?: string | null;
}): JsonLd {
  const author = resolveAuthorProfile(authorName);
  return {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline,
    description,
    inLanguage,
    mainEntityOfPage: `${SITE_URL}${path}`,
    author: authorSchemaReference(author),
    publisher: {
      '@id': ORGANIZATION_ID,
    },
    isPartOf: { '@id': WEBSITE_ID },
  };
}

export function buildFaqSchema(
  items: Array<{ question: string; answer: string }>,
  options?: { canonicalPath?: string; inLanguage?: 'tr-TR' | 'en-US'; locale?: SchemaLocale },
): JsonLd {
  const absoluteUrl = options?.canonicalPath
    ? absoluteUrlFromCanonicalPath(options.canonicalPath, options.locale)
    : undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    ...(absoluteUrl ? { '@id': `${absoluteUrl}#faq` } : {}),
    ...(options?.inLanguage ? { inLanguage: options.inLanguage } : {}),
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export function buildHowToSchema({
  name,
  description,
  canonicalPath,
  steps,
  inLanguage,
}: {
  name: string;
  description: string;
  canonicalPath: string;
  steps: Array<{ name: string; text: string }>;
  inLanguage?: 'tr-TR' | 'en-US';
}): JsonLd {
  const absoluteUrl = canonicalPath === '/' ? SITE_URL : `${SITE_URL}${canonicalPath}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    '@id': `${absoluteUrl}#howto`,
    name,
    description,
    ...(inLanguage ? { inLanguage } : {}),
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  };
}

function authorSchemaReference(author: AuthorProfile): { '@id': string } {
  if (isBrandAuthorSlug(author.slug)) {
    return { '@id': ORGANIZATION_ID };
  }
  return { '@id': personId(author.slug) };
}

export function buildAuthorGraphNodes(authorName?: string | null, lang: 'tr' | 'en' = 'en'): JsonLd[] {
  const author = resolveAuthorProfile(authorName);
  if (isBrandAuthorSlug(author.slug)) {
    return [];
  }
  return [buildPersonSchema(author, lang)];
}

export function buildAuthorGraphNodesBySlug(slug: string, lang: 'tr' | 'en' = 'en'): JsonLd[] {
  const author = getAuthorBySlug(slug);
  if (!author) return buildAuthorGraphNodes(undefined, lang);
  if (isBrandAuthorSlug(author.slug)) {
    return [];
  }
  return [buildPersonSchema(author, lang)];
}
